import { useState, type ChangeEvent } from 'react';
import { Avatar, Tag, App } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import type { AuthUser } from '../../../auth/storage';
import {
    compressAvatar,
    saveStoredAvatar,
    useStoredAvatar,
} from '../../../auth/avatarStorage';
import { useTranslation } from 'react-i18next';
import '../Account.css';

type ProfileHeroProps = {
    user: AuthUser;
};

function getInitials(name: string) {
    return name
        .split(' ')
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

function statusColor(status?: string) {
    const value = status?.toLowerCase();
    if (value === 'active') return 'success';
    if (value === 'pending' || value === 'inactive') return 'warning';
    if (value === 'banned' || value === 'disabled') return 'error';
    return 'default';
}

function ProfileHero({ user }: ProfileHeroProps) {
    const { t } = useTranslation('account');
    const { message } = App.useApp();
    const avatar = useStoredAvatar(user);
    const [uploading, setUploading] = useState(false);
    const displayName = String(user.displayName || user.name || user.email || t('heading.title'));
    const email = typeof user.email === 'string' ? user.email : '';
    const status = typeof user.status === 'string' ? user.status : '';

    const changeAvatar = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        setUploading(true);
        try {
            const dataUrl = await compressAvatar(file);
            saveStoredAvatar(user, dataUrl);
            message.success(t('messages.avatarUpdated'));
        } catch (error) {
            message.error(
                error instanceof Error ? error.message : t('messages.avatarUpdateFailed')
            );
        } finally {
            setUploading(false);
        }
    };

    return (
        <section className="account-hero" aria-label={t('a11y.profileOverview')}>
            <label
                className={`account-hero__avatar-picker${uploading ? ' is-loading' : ''}`}
                aria-label={t('actions.changeAvatar')}
                title={t('actions.changeAvatar')}
            >
                <Avatar className="account-hero__avatar" size={56} src={avatar || undefined}>
                    {getInitials(displayName)}
                </Avatar>
                <span className="account-hero__avatar-edit" aria-hidden>
                    <CameraOutlined />
                </span>
                <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={uploading}
                    onChange={changeAvatar}
                />
            </label>
            <div className="account-hero__meta">
                <div className="account-hero__name-row">
                    <h2>{displayName}</h2>
                    {status ? (
                        <Tag color={statusColor(status)} className="account-hero__status">
                            {status}
                        </Tag>
                    ) : null}
                </div>
                {email ? <p>{email}</p> : null}
            </div>
        </section>
    );
}

export default ProfileHero;
